import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';

import SecondaryPageLayout from '@/Layout/SecondaryPageLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import useCachedUserData from '@/hooks/use-cached-user-data';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';

import { 
  getVerificationSetupProgress, 
  initializeVerificationSetup,
  finalizeVerificationSetup
} from '@/apis/experience/verification-setup';
import VerificationProgressIndicator from './components/VerificationProgressIndicator';
import EmailVerificationStep from './components/EmailVerificationStep';
import WhatsAppVerificationStep from './components/WhatsAppVerificationStep';
import TotpSetupStep from './components/TotpSetupStep';
import DeviceRegistrationStep from './components/DeviceRegistrationStep';
import StepCard from './components/StepCard';
import useSetupProgress from './hooks/useSetupProgress';
import { type VerificationStep, type VerificationStepType } from './types';
import { EmailIcon, WhatsAppIcon, AuthenticatorIcon, DeviceIcon } from './icons';

import styles from './index.module.scss';

const MfaCompleteSetup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { experienceSettings } = useContext(PageContext);
  const handleError = useErrorHandler();
  
  // Get user ID from URL parameters (from OAuth callback)
  const userId = searchParams.get('user_id');

  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const { userData, fetchUserData, isRateLimited, getRateLimitRetryTime } = useCachedUserData();

  const {
    progress,
    currentStep,
    nextStep,
    completionPercentage,
    isSetupCompleted,
    initializeProgress,
    refreshProgress,
    completeStep
  } = useSetupProgress();

  // Define the verification steps with proper SVG icons
  const verificationSteps: VerificationStep[] = [
    {
      id: 'email',
      title: t('mfa.email_verification'),
      description: t('mfa.email_verification_description'),
      status: progress?.emailVerified ? 'completed' : 
              currentStep === 'email' ? 'in-progress' : 'pending',
      icon: <EmailIcon />,
      component: EmailVerificationStep,
    },
    {
      id: 'whatsapp',
      title: t('mfa.whatsapp_verification'),
      description: t('mfa.whatsapp_verification_description'),
      status: progress?.whatsappVerified ? 'completed' : 
              currentStep === 'whatsapp' ? 'in-progress' : 'pending',
      icon: <WhatsAppIcon />,
      component: WhatsAppVerificationStep,
    },
    {
      id: 'totp',
      title: t('mfa.authenticator_setup'),
      description: t('mfa.authenticator_setup_description'),
      status: progress?.totpSetup ? 'completed' : 
              currentStep === 'totp' ? 'in-progress' : 'pending',
      icon: <AuthenticatorIcon />,
      component: TotpSetupStep,
    },
    {
      id: 'device',
      title: t('mfa.device_registration'),
      description: t('mfa.device_registration_description'),
      status: progress?.deviceRegistered ? 'completed' : 
              currentStep === 'device' ? 'in-progress' : 'pending',
      icon: <DeviceIcon />,
      component: DeviceRegistrationStep,
    },
  ];

  // Initialize setup when component mounts
  useEffect(() => {
    // Redirect to sign-in if no user ID provided
    if (!userId) {
      navigate('/sign-in', { replace: true });
      return;
    }

    const initSetup = async () => {
      setIsInitializing(true);
      try {
        // Use cached user data to prevent duplicate API calls
        await fetchUserData(userId, true);

        // Only initialize setup if we're not rate limited
        if (!isRateLimited()) {
          // Then initialize verification setup with valid session
          await initializeProgress();
          await refreshProgress();
        }
      } catch (error) {
        // Handle session errors - redirect to start fresh
        if (error instanceof Error && (
          error.message?.includes('session not found') || 
          error.message?.includes('Session not found') ||
          error.message?.includes('interaction session id cookie not found') ||
          error.message?.includes('Internal server error') ||
          error.message?.includes('500')
        )) {
          console.error('Session expired or invalid user ID! Redirecting to error page...');
          window.location.href = '/error?message=invalid_session';
          return;
        }
        
        // Handle errors but don't redirect immediately if rate limited
        if (error instanceof Error && !error.message.includes('Too many attempts')) {
          await handleError(error);
          // Add a flag to prevent redirect loops
          const redirectKey = `redirect_from_setup_${userId}`;
          const lastRedirect = sessionStorage.getItem(redirectKey);
          const now = Date.now();
          
          // Only redirect if we haven't redirected in the last 5 seconds
          if (!lastRedirect || now - parseInt(lastRedirect, 10) > 5000) {
            sessionStorage.setItem(redirectKey, String(now));
            navigate(`/verification-choose?user_id=${userId}`, { replace: true });
          }
        }
      } finally {
        setIsInitializing(false);
        setIsLoading(false);
      }
    };

    void initSetup();
  }, [userId, navigate, initializeProgress, refreshProgress, handleError, fetchUserData, isRateLimited]);

  // Update current step index based on progress
  useEffect(() => {
    if (!currentStep || !progress) return;

    const stepIndex = verificationSteps.findIndex(step => step.id === currentStep);
    if (stepIndex !== -1) {
      setCurrentStepIndex(stepIndex);
    }
  }, [currentStep, progress]);

  // Handle step completion
  const handleStepComplete = useCallback(async (stepType: VerificationStepType) => {
    try {
      await completeStep(stepType, {});
      await refreshProgress();

      // If setup is complete, finalize and redirect
      if (isSetupCompleted) {
        await finalizeVerificationSetup({ confirmCompletion: true });
        navigate('/dashboard'); // TODO: Make redirect configurable
      }
    } catch (error) {
      await handleError(error);
    }
  }, [completeStep, refreshProgress, isSetupCompleted, handleError, navigate]);

  // Handle navigation to next step
  const handleNextStep = useCallback(() => {
    if (currentStepIndex < verificationSteps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  }, [currentStepIndex, verificationSteps.length]);

  // Show rate limit error if present
  if (isRateLimited()) {
    const retryTime = getRateLimitRetryTime();
    const retryMinutes = retryTime ? Math.ceil(retryTime / 60000) : 10;
    
    return (
      <SecondaryPageLayout title="mfa.complete_security_setup" description="mfa.complete_security_setup_description">
        <div className={styles.errorContainer}>
          <h3>Too Many Attempts</h3>
          <p>You've made too many attempts. Please try again in {retryMinutes} minutes.</p>
          <button
            className={styles.retryButton}
            onClick={() => {
              navigate(`/verification-choose?user_id=${userId}`, { replace: true });
            }}
          >
            Go Back
          </button>
        </div>
      </SecondaryPageLayout>
    );
  }

  if (isLoading || isInitializing) {
    return (
      <SecondaryPageLayout title="mfa.initializing" description="mfa.initializing_description">
        <div className={styles.loadingContainer}>
          <ClipLoader
            color="#1976d2"
            loading={true}
            size={40}
            aria-label="Loading Setup"
            data-testid="setup-loader"
          />
          <div className={styles.loadingText}>
            {t('mfa.preparing_security_setup')}
          </div>
        </div>
      </SecondaryPageLayout>
    );
  }

  const currentStepData = verificationSteps[currentStepIndex];
  const CurrentStepComponent = currentStepData?.component;

  return (
    <SecondaryPageLayout 
      title="mfa.complete_security_setup" 
      description="mfa.complete_security_setup_description"
    >
      <div className={styles.setupContainer}>
        {/* User Account Info */}
        {userData?.email && (
          <div className={styles.accountInfo}>
            <div className={styles.accountHeader}>
              <span className={styles.accountLabel}>Securing account:</span>
              <span className={styles.accountEmail}>{userData.email}</span>
            </div>
            {userData.phone && (
              <div className={styles.accountPhone}>
                <span className={styles.phoneLabel}>Phone:</span>
                <span className={styles.phoneValue}>{userData.phone}</span>
              </div>
            )}
          </div>
        )}

        {/* Progress Indicator */}
        <div className={styles.progressSection}>
          <VerificationProgressIndicator
            steps={verificationSteps}
            currentStepIndex={currentStepIndex}
            completionPercentage={completionPercentage}
          />
        </div>

        {/* Current Step Content */}
        <div className={styles.stepContent}>
          {CurrentStepComponent && (
            <CurrentStepComponent
              step={currentStepData}
              onStepComplete={handleStepComplete}
              onNext={handleNextStep}
              progress={progress}
              userEmail={userData?.email ?? ''}
              userPhone={userData?.phone ?? ''}
            />
          )}
        </div>

        {/* Setup Summary */}
        {isSetupCompleted && (
          <div className={styles.completionSection}>
            <div className={styles.completionMessage}>
              <h3>{t('mfa.setup_completed_title')}</h3>
              <p>{t('mfa.setup_completed_description')}</p>
            </div>
            <button
              className={styles.continueButton}
              onClick={() => {
                void finalizeVerificationSetup({ confirmCompletion: true })
                  .then(() => navigate('/dashboard'));
              }}
            >
              {t('mfa.continue_to_application')}
            </button>
          </div>
        )}

        {/* Debug Info (Development Only) */}
        {process.env.NODE_ENV === 'development' && (
          <div className={styles.debugInfo}>
            <h4>Debug Info:</h4>
            <pre>{JSON.stringify({ 
              currentStep, 
              nextStep, 
              completionPercentage,
              progress 
            }, null, 2)}</pre>
          </div>
        )}
      </div>
    </SecondaryPageLayout>
  );
};

export default MfaCompleteSetup;